import config from "@/config";
import { GetObjectCommand, PutObjectCommand } from "@aws-sdk/client-s3";

const MAX_ATTEMPTS = 3;

// Status codes S3 returns when an IfMatch write loses a race with another write
const CONFLICT_STATUS_CODES = [409, 412];

// Updates a deposit metadata file (private/<identityId>/deposits/<depositId>).
//
// The file is also written by the ingest workflow (ingestStatus), so rather than
// writing back a copy loaded with the page, the current file is re-read and
// `update` applied to it. The write is conditional on the file not having
// changed since that read, and retried if it has.
//
// `update` is passed the current metadata to modify in place, and can return
// false to abort. Resolves to the written metadata, or null if aborted.
export async function updateDepositMetadata(client, key, update) {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const current = await client.send(new GetObjectCommand({
      Bucket: config.bucketName,
      Key: key,
      ResponseCacheControl: "no-store", // never apply the update to a cached copy
    }));
    const metadata = JSON.parse(await current.Body.transformToString());

    if (update(metadata) === false) return null;

    try {
      await client.send(new PutObjectCommand({
        Bucket: config.bucketName,
        Key: key,
        Body: JSON.stringify(metadata),
        CacheControl: "no-store, max-age=0, must-revalidate",
        ContentType: "application/json",
        IfMatch: current.ETag,
      }));
      return metadata;
    } catch (err) {
      if (!CONFLICT_STATUS_CODES.includes(err?.$metadata?.httpStatusCode)) throw err;
      console.warn(`${key} changed while being updated (attempt ${attempt}), retrying`);
    }
  }
  throw new Error(`${key} kept changing while being updated`);
}

// Clears the ingest status (and Workbench URL) reported by the ingest workflow
// for a previous submission, so it isn't shown against a new one
export function clearIngestStatus(metadata) {
  delete metadata.ingestStatus;
  delete metadata.ingestStatusLastUpdated;
  delete metadata.workbenchUrl;
}
